import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Building2, Cable, CheckCircle2, Circle, Clock, CreditCard, FolderKanban, Users } from 'lucide-react';
import { EXTENSION_INSTALL_PATH } from '@vd/types';
import { api, type DashboardSystems, type WorkspaceSummary as Summary } from '../../lib/api';
import { When } from '../../lib/When';
import { formatDay } from '../../lib/time';
import { formatBytes } from '../dashboard/format';
import { Card, toneStyle, type Tone } from '../dashboard/DashboardCard';
import styles from '../dashboard/DashboardSections.module.css';

/*
 * Reports' Workspace page (Jason, 16 Oct): Connected systems and Plan usage,
 * which were the Dashboard's Workspace group. An owner's: the page shows
 * them to owners only, and the server refuses both to anyone else.
 */

/** One line of Connected systems: what it is, how it stands, and what to do when it needs attention. */
/**
 * One system's line: a check only when it is connected and working, a warning
 * when it needs attention, and an empty circle when it is not set up at all
 * (Jason, 17 Oct: Jira not connected showed a check).
 */
function SystemLine({ label, status, attention, off, action }: { label: string; status: ReactNode; attention?: boolean; off?: boolean; action?: ReactNode }) {
  return (
    <li className={styles.systemLine} data-attention={attention || undefined} data-off={off || undefined} data-testid="system-line">
      {attention
        ? <AlertTriangle size={14} className={styles.warn} aria-label="Needs attention" />
        : off
          ? <Circle size={14} className={styles.offIcon} aria-label="Not set up" />
          : <CheckCircle2 size={14} className={styles.okIcon} aria-label="Connected" />}
      <span className={styles.systemLabel}>{label}</span>
      <span className={styles.systemStatus}>{status}</span>
      {action}
    </li>
  );
}

/**
 * Connected systems (16 Oct, was Setup that needs attention): the extension,
 * Jira, widgets, the AI agents at work and whether AI is on (the models in
 * use left out, 17 Oct: not something customers need), with
 * anything that needs attention marked and counted in the header. An owner's,
 * on Reports' Workspace page (Jason, 16 Oct).
 */
export function ConnectedSystems({ companyId }: { companyId: string }) {
  const { data } = useQuery({ queryKey: ['dashboard', companyId, 'systems'], queryFn: () => api.dashboard.systems(companyId) });
  if (!data) return null;
  const attention = systemsNeedingAttention(data);
  return (
    <Card title="Connected systems" testId="dash-systems" icon={Cable} tone="cyan" size="wide"
      more={attention > 0 ? <span className={styles.attentionCount}>{attention} {attention === 1 ? 'needs' : 'need'} attention</span> : undefined}>
      <ul className={styles.systems}>
        <SystemLine label="Extension"
          status={data.extension.installed
            ? `${data.extension.installed} installed${data.extension.outdated ? `; ${data.extension.latest} is out` : ''}`
            : 'Not seen yet'}
          attention={data.extension.outdated}
          off={!data.extension.installed}
          action={data.extension.outdated || !data.extension.installed
            ? <Link to={EXTENSION_INSTALL_PATH} className={styles.more}>{data.extension.installed ? 'How to update' : 'Get it'}</Link>
            : undefined} />
        <SystemLine label="Jira"
          status={data.jira.connected ? (data.jira.site ? data.jira.site.replace(/^https?:\/\//, '') : 'Connected') : 'Not connected'}
          attention={data.jira.needsReconnect}
          off={!data.jira.connected}
          action={data.jira.needsReconnect || !data.jira.connected
            ? <Link to="/company/settings/jira" className={styles.more}>{data.jira.needsReconnect ? 'Connect again' : 'Connect'}</Link>
            : undefined} />
        {data.widgets.map((widget) => (
          <SystemLine key={widget.projectId} label={`Widget on ${widget.projectName}`}
            status={widget.lastSeenAt ? <When at={widget.lastSeenAt} prefix="seen " /> : 'not seen yet'}
            attention={widget.quiet}
            off={!widget.lastSeenAt && !widget.quiet}
            action={widget.quiet ? <Link to={`/projects/${widget.projectId}/settings?tab=widget`} className={styles.more}>Install settings</Link> : undefined} />
        ))}
        <SystemLine label="AI agents"
          status={<>
            {data.agents.length === 0 ? `None at work in the last ${data.agentsSeenDays} days`
              : data.agents.map((agent) => agent.agent).join(', ')}
            {data.mine.agents > 0 && ` · you have ${data.mine.agents} connected`}
          </>}
          off={data.agents.length === 0 && data.mine.agents === 0}
          action={data.mine.agents === 0 ? <Link to="/me/settings/mcp-setup" className={styles.more}>Connect an agent</Link> : undefined} />
        <SystemLine label="AI in this workspace" status={data.ai.enabled ? `On${data.ai.transcription ? ', with transcription' : ''}` : 'Off'}
          off={!data.ai.enabled}
          action={!data.ai.enabled ? <Link to="/company/settings/ai" className={styles.more}>AI settings</Link> : undefined} />
      </ul>
    </Card>
  );
}

/** How many lines of Connected systems need attention. */
export function systemsNeedingAttention(data: DashboardSystems): number {
  return Number(data.extension.outdated) + Number(data.jira.needsReconnect) + data.widgets.filter((widget) => widget.quiet).length;
}

/** Each kind of usage keeps its colour, and turns amber, then red, near the limit. */
const USAGE_TONE: Record<string, Tone> = { Tickets: 'blue', 'Recording minutes': 'violet', 'Narration minutes': 'magenta', Storage: 'cyan' };

export function PlanUsage({ companyId }: { companyId: string }) {
  const { data } = useQuery({ queryKey: ['dashboard', companyId, 'plan-usage'], queryFn: () => api.dashboard.planUsage(companyId) });
  if (!data) return null;
  const bars: Array<[string, number | null, number | null, (value: number) => string]> = [
    ['Tickets', data.tickets.used, data.tickets.limit, String],
    ['Recording minutes', data.recordingMinutes.used, data.recordingMinutes.limit, String],
    ['Narration minutes', data.narrationMinutes.used, data.narrationMinutes.limit, String],
    ['Storage', data.storageBytes.used, data.storageBytes.limit, formatBytes],
  ];
  const shown = bars.filter(([, used, limit]) => used !== null && limit !== null && limit > 0);
  if (shown.length === 0) return null;
  const nearLimit = shown.some(([, used, limit]) => (used ?? 0) / (limit ?? 1) >= 0.8);
  return (
    <Card title="Plan usage this month" testId="dash-plan-usage" icon={Clock} tone="green"
      more={nearLimit ? <Link to="/company/settings/billing" className={styles.more}>Upgrade</Link> : undefined}>
      <ul className={styles.rows}>
        {shown.map(([label, used, limit, format]) => {
          const share = Math.min(1, (used ?? 0) / (limit ?? 1));
          return (
            <li key={label} className={styles.usage} style={toneStyle(USAGE_TONE[label] ?? 'blue')}>
              <span className={styles.rowTitle}>{label}</span>
              <span className={styles.rowMeta}>{format(used ?? 0)} of {format(limit ?? 0)}</span>
              <span className={styles.bar} aria-hidden>
                <span style={{ width: `${Math.round(share * 100)}%` }} data-level={share >= 1 ? 'full' : share >= 0.8 ? 'near' : undefined} />
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

const PLAN_NAMES: Record<string, string> = { free: 'Free', personal: 'Personal', team: 'Team', business: 'Business', enterprise: 'Enterprise' };
const dollars = (cents: number) => `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: cents % 100 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`;
// The billing date is a calendar day, stored as its UTC midnight: read in UTC, or a zone west of it shows the day before.
const day = (at: string) => formatDay(at);

/**
 * The workspace at a glance (Jason, 16 Oct), under Plan usage: what it costs
 * a month at list price and what makes that up, when it renews, its people by
 * role and the seats they fill, its projects, and who was active lately.
 */
/**
 * What a month costs, as one bar split into its parts (16 Oct): the plan,
 * add-ons and the widget, each its share of the total, each named with its
 * amount beside it. A part of nothing is left out; so is the bar, when there
 * is nothing to pay.
 */
function CostBar({ cents }: { cents: NonNullable<Summary['monthlyCents']> }) {
  const parts = [
    { key: 'plan', label: 'Plan', amount: cents.plan ?? 0, tone: 'var(--chart-violet)' },
    { key: 'addons', label: 'Add-ons', amount: cents.addons, tone: 'var(--chart-blue)' },
    { key: 'widget', label: 'Widget', amount: cents.widget, tone: 'var(--chart-cyan)' },
  ].filter((part) => part.amount > 0);
  const total = parts.reduce((sum, part) => sum + part.amount, 0);
  if (total <= 0) return null;
  const said = parts.map((part) => `${part.label} ${dollars(part.amount)}`).join(', ');
  return (
    <div className={styles.costBar} data-testid="dash-cost-bar">
      <div className={styles.costTrack} role="img" aria-label={`Each month: ${said}`}>
        {parts.map((part) => (
          <span key={part.key} data-part={part.key} style={{ width: `${(part.amount / total) * 100}%`, background: part.tone }} title={`${part.label}: ${dollars(part.amount)}, ${Math.round((part.amount / total) * 100)}%`} />
        ))}
      </div>
      <ul className={styles.costKey} aria-hidden="true">
        {parts.map((part) => <li key={part.key}><span style={{ background: part.tone }} />{part.label} {dollars(part.amount)}</li>)}
      </ul>
    </div>
  );
}

export function WorkspaceAtAGlance({ companyId }: { companyId: string }) {
  const { data } = useQuery({ queryKey: ['dashboard', companyId, 'workspace-summary'], queryFn: () => api.dashboard.workspaceSummary(companyId) });
  if (!data) return null;
  return (
    <Card title="Plan and people" testId="dash-workspace-summary" icon={Building2} tone="violet">
      <div className={styles.glance}>
        <div className={styles.glanceFigure} style={toneStyle('green')}>
          <CreditCard size={15} className={styles.figureIcon} aria-hidden="true" />
          <span className={styles.figureValue}>{data.monthlyCents?.total != null ? dollars(data.monthlyCents.total) : PLAN_NAMES[data.plan] ?? data.plan}</span>
          <span className={styles.figureLabel}>
            {data.monthlyCents?.total != null ? 'a month as it renews, at list price' : data.endsAtPeriodEnd ? 'nothing renews' : data.plan === 'enterprise' ? 'billed by arrangement' : 'nothing to pay'}
          </span>
        </div>
        <div className={styles.glanceFigure} style={toneStyle('blue')}>
          <Users size={15} className={styles.figureIcon} aria-hidden="true" />
          <span className={styles.figureValue}>{data.people.total}</span>
          <span className={styles.figureLabel}>{data.people.total === 1 ? 'person' : 'people'}, {data.activeLast30Days} active here in 30 days</span>
        </div>
        <div className={styles.glanceFigure} style={toneStyle('violet')}>
          <FolderKanban size={15} className={styles.figureIcon} aria-hidden="true" />
          <span className={styles.figureValue}>{data.projects}</span>
          <span className={styles.figureLabel}>{data.projects === 1 ? 'project' : 'projects'}</span>
        </div>
      </div>
      {data.monthlyCents && <CostBar cents={data.monthlyCents} />}
      <dl className={styles.glanceFacts}>
        <dt>Plan</dt>
        <dd>
          {PLAN_NAMES[data.plan] ?? data.plan}{data.interval ? `, billed ${data.interval === 'annual' ? 'yearly' : 'monthly'}` : ''}
          {data.renewsAt && <> · {data.endsAtPeriodEnd ? 'ends' : 'renews'} {day(data.renewsAt)}</>}
        </dd>
        {data.monthlyCents && <>
          <dt>Made up of</dt>
          <dd>
            {[
              data.monthlyCents.plan !== null ? `plan ${dollars(data.monthlyCents.plan)}` : data.endsAtPeriodEnd ? 'plan ends' : data.plan === 'enterprise' ? 'plan billed by arrangement' : 'no paid plan',
              data.monthlyCents.addons > 0 ? `add-ons ${dollars(data.monthlyCents.addons)}` : null,
              data.monthlyCents.widget > 0 ? `widget ${dollars(data.monthlyCents.widget)}` : null,
            ].filter(Boolean).join(' · ')}
          </dd>
        </>}
        <dt>People</dt>
        <dd>
          {[
            `${data.people.owners} ${data.people.owners === 1 ? 'owner' : 'owners'}`,
            `${data.people.members} ${data.people.members === 1 ? 'member' : 'members'}`,
            data.people.viewers > 0 ? `${data.people.viewers} ${data.people.viewers === 1 ? 'viewer' : 'viewers'}` : null,
          ].filter(Boolean).join(' · ')}
        </dd>
        {data.seats && <>
          <dt>Seats</dt>
          <dd>{data.seats.used} of {data.seats.cap} used{data.seats.openInvites > 0 ? `, ${data.seats.openInvites} invited` : ''}</dd>
        </>}
      </dl>
      <div className={styles.glanceLinks}>
        <Link to="/company/settings/billing" className={styles.more}>Billing</Link>
        <Link to="/company/settings/members" className={styles.more}>Members</Link>
      </div>
    </Card>
  );
}
